// src/components/common/search/activeFilterCount.ts

export type IsActiveFn<TSearch> = (value: unknown, search: TSearch) => boolean;

/**
 * Default heuristic to determine whether a filter value is "active".
 *
 * Notes:
 * - Numbers: any finite number (including 0) is treated as active.
 * - Booleans: both true/false are treated as "set" (active). If you want
 *   "true only", provide an override.
 * - Objects: treated as active when they have at least one own enumerable key.
 *   (Use overrides for nested/structured filters if needed.)
 */
export const defaultIsActive = (
  value: unknown,
  emptyStringValues: string[] = [],
): boolean => {
  if (value === undefined || value === null) return false;

  // string
  if (typeof value === 'string') {
    const v = value.trim();
    if (v === '') return false;
    if (emptyStringValues.includes(v)) return false;
    return true;
  }

  // number (treat 0 as active by default; override if needed)
  if (typeof value === 'number') {
    return Number.isFinite(value);
  }

  // boolean (true/false are both considered "set"; override if you want true-only)
  if (typeof value === 'boolean') {
    return true;
  }

  // array
  if (Array.isArray(value)) {
    return value.length > 0;
  }

  // date
  if (value instanceof Date) {
    return !Number.isNaN(value.getTime());
  }

  // object
  if (typeof value === 'object') {
    return Object.keys(value as Record<string, unknown>).length > 0;
  }

  return true;
};

/**
 * Count active filters in a search object.
 *
 * - Per-key overrides can be provided to apply custom "is active" rules.
 * - This utility assumes the search object contains filter fields only
 *   (avoid mixing pagination/sort/internal flags unless intended).
 */
export const countActiveFilters = <TSearch extends Record<string, unknown>>(
  search: TSearch,
  overrides?: Partial<Record<keyof TSearch, IsActiveFn<TSearch>>>,
  emptyStringValues: string[] = [],
): number => {
  let count = 0;

  (Object.keys(search) as Array<keyof TSearch>).forEach((key) => {
    const value = search[key];
    const override = overrides?.[key];
    const active = override
      ? override(value, search)
      : defaultIsActive(value, emptyStringValues);

    if (active) count += 1;
  });

  return count;
};
